// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Color } from '../../../Graphics';
import { PriceType } from '@shared/utils/History/CashItemUtils';
import { MAMode } from '../IndicatorConstants';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';
import { InputParameterCombobox } from '../InputParamaterClasses/InputParameterCombobox';
export class ATR extends IndicatorScriptBase {
    constructor() {
        super();
        this.ProjectName = 'Average True Range';
        this.Comments = 'Measures market volatility';
        super.SetIndicatorLine('Line', Color.Blue, 1, LineStyle.SimpleChart);
        this.SeparateWindow = true;
        this.MAPeriod = 13;
        super.InputParameter(new InputParameterInteger('MAPeriod', 'Period of Moving Average', 0, 1, 9999));
        this.MAType = MAMode.SMA;
        super.InputParameter(new InputParameterCombobox('MAType', 'Type of moving average', 1, [
            { Simple: MAMode.SMA },
            { Exponential: MAMode.EMA },
            { Modified: MAMode.SMMA },
            { 'Linear Weighted': MAMode.LWMA }
        ]));
    }
    Init() {
        this.IndicatorShortName('ATR(' + this.MAPeriod + ')');
    }
    GetIndicatorShortName() {
        return 'ATR(' + this.MAPeriod + ')';
    }
    getCustomName() {
        return 'ATR (' + this.ProjectName + ')';
    }
    OnQuote() {
        if (this.CurrentData.Count < this.MAPeriod) {
            return;
        }
        switch (this.MAType) {
            case MAMode.SMA:
                this.SetValue(0, 0, this.GetSMA());
                break;
            case MAMode.EMA:
                this.SetValue(0, 0, this.GetEMA());
                break;
            case MAMode.SMMA:
                this.SetValue(0, 0, this.GetMMA());
                break;
            case MAMode.LWMA:
                this.SetValue(0, 0, this.GetLWMA());
                break;
        }
    }
    GetTrueRange(index) {
        const CurrentData = this.CurrentData;
        const hi = CurrentData.getPriceByType(PriceType.High, index);
        const lo = CurrentData.getPriceByType(PriceType.Low, index);
        const prevClose = CurrentData.Count > index + 1
            ? CurrentData.getPriceByType(PriceType.Close, index + 1)
            : CurrentData.getPriceByType(PriceType.Close, index);
        return Math.max(hi - lo, Math.max(Math.abs(prevClose - hi), Math.abs(prevClose - lo)));
    }
    GetSMA() {
        let summa = 0; // Sum of prices
        // Loop of calculation.
        for (let i = 0; i < this.MAPeriod; i++) {
            summa += this.GetTrueRange(i);
        }
        // Returning current value of the SMA
        return summa / this.MAPeriod;
    }
    GetEMA() {
        // Calculation of a coefficient
        const k = 2 / (this.MAPeriod + 1);
        // Sum of prices
        let summa = this.GetTrueRange(this.MAPeriod - 1);
        for (let i = this.MAPeriod - 2; i >= 0; i--) {
            summa += k * (this.GetTrueRange(i) - summa);
        }
        // Returning value
        return summa;
    }
    GetMMA() {
        // Current price
        let mma = this.GetTrueRange(0);
        // coefficient
        const k = 1 / this.MAPeriod;
        // Loop of calculation.
        for (let i = 0; i < this.MAPeriod && mma > 0; i++) {
            mma = this.GetTrueRange(i) * k + mma * (1 - k);
        }
        // Returning of mma
        return mma;
    }
    GetLWMA() {
        // Numerator of the rate
        let numerator = 0;
        // Denominator of the rate
        let denominator = 0;
        // period counter
        let period = this.MAPeriod;
        // Loop of calculation.
        for (let i = 0; i < this.MAPeriod; i++) {
            numerator += period * this.GetTrueRange(i);
            denominator += period;
            period--;
        }
        return denominator === 0 ? 0 : numerator / denominator;
    }
}
